import {DymekEvidenceClass} from './evidence-policy.js';

function text(value,max=240){
  const result=String(value??'').trim();
  return result?result.slice(0,max):null;
}
function instant(value){
  const raw=String(value??'').trim();
  const parsed=Date.parse(raw);
  return raw&&!Number.isNaN(parsed)?new Date(parsed).toISOString():null;
}
function number(value){
  const n=Number(value);
  return Number.isFinite(n)?n:null;
}
function stageOrder(a,b){
  return Number(a?.sortOrder??a?.orderIndex??a?.position??0)
    -Number(b?.sortOrder??b?.orderIndex??b?.position??0);
}
function targetFor(bundle,stageId){
  return (bundle?.stageTargets||[]).find(row=>String(row.stageId||row.recipeStageId||'')===String(stageId))||null;
}
function activeStage(bundle){
  const session=bundle?.session||{};
  const id=session.activeStageId;
  if(id)return (bundle?.stages||[]).find(stage=>String(stage.id)===String(id))||null;
  const transitions=[...(bundle?.liveStageTransitions||[])].sort((a,b)=>
    Date.parse(b?.occurredAt||b?.startedAt||0)-Date.parse(a?.occurredAt||a?.startedAt||0)
  );
  const latest=transitions.find(row=>row?.stageId);
  if(latest)return (bundle?.stages||[]).find(stage=>String(stage.id)===String(latest.stageId))||null;
  return null;
}
function latestByKind(rows=[]){
  const map=new Map();
  for(const row of [...rows].sort((a,b)=>
    Date.parse(b?.recordedAt||0)-Date.parse(a?.recordedAt||0)
  )){
    const kind=String(row?.readingKind||row?.reading_kind||'').trim().toLowerCase();
    if(kind&&!map.has(kind))map.set(kind,row);
  }
  return map;
}
function observed(content,refs,{historical=false}={}){
  return Object.freeze({
    content,
    evidenceClass:DymekEvidenceClass.OBSERVED,
    safetyCritical:false,
    historical,
    evidenceRefs:Object.freeze(refs),
    assumptions:Object.freeze([])
  });
}
function stageRef(sessionId,stageId,observedAt=null){
  return Object.freeze({
    kind:'stage',
    ref:String(sessionId)+'/'+String(stageId),
    observedAt:observedAt||null
  });
}
function sessionRef(sessionId,observedAt=null){
  return Object.freeze({kind:'session',ref:String(sessionId),observedAt:observedAt||null});
}

export function buildActiveSessionFacts(bundle,{
  now=()=>Date.now(),
  staleReadingMinutes=15
}={}){
  const session=bundle?.session;
  if(!session?.id)throw new TypeError('Session bundle is required');

  const transitions=[...(bundle?.liveStageTransitions||[])]
    .filter(row=>row?.stageId)
    .sort((a,b)=>Date.parse(b?.occurredAt||b?.startedAt||0)-Date.parse(a?.occurredAt||a?.startedAt||0));
  const sessionStageId=String(session.activeStageId||'').trim()||null;
  const transitionStageId=String(transitions[0]?.stageId||'').trim()||null;
  const activeStageConflict=Boolean(
    sessionStageId&&transitionStageId&&sessionStageId!==transitionStageId
  );
  const stage=activeStageConflict?null:activeStage(bundle);
  const target=stage?targetFor(bundle,stage.id):null;
  const stageStartedAt=instant(
    session.activeStageStartedAt
    ||(bundle?.liveStageTransitions||[]).find(row=>String(row?.stageId||'')===String(stage?.id||''))?.startedAt
  );
  const nowMillis=typeof now==='function'?Number(now()):Number(now);
  const elapsedMinutes=stageStartedAt&&Number.isFinite(nowMillis)
    ?Math.max(0,Math.floor((nowMillis-Date.parse(stageStartedAt))/60000))
    :null;

  const readings=latestByKind(bundle?.temperatureReadings||[]);
  const latestTemperatures={};
  for(const [kind,row] of readings){
    const recordedAt=instant(row.recordedAt||row.recorded_at);
    const valueCelsius=number(row.valueCelsius??row.value_c);
    if(!recordedAt||valueCelsius==null)continue;
    const ageMinutes=Number.isFinite(nowMillis)
      ?Math.max(0,Math.floor((nowMillis-Date.parse(recordedAt))/60000))
      :null;
    latestTemperatures[kind]=Object.freeze({
      valueCelsius,
      recordedAt,
      sourceType:text(row.sourceType||row.source_type,40),
      ageMinutes,
      stale:ageMinutes==null?true:ageMinutes>staleReadingMinutes
    });
  }

  const recentEvents=[...(bundle?.liveEvents||[])]
    .sort((a,b)=>Date.parse(b?.occurredAt||0)-Date.parse(a?.occurredAt||0))
    .slice(0,8)
    .map(row=>Object.freeze({
      eventType:text(row.eventType||row.event_type,40),
      occurredAt:instant(row.occurredAt||row.occurred_at)
    }))
    .filter(row=>row.eventType&&row.occurredAt);

  return Object.freeze({
    sessionId:String(session.id),
    status:text(session.status,30),
    actualStartAt:instant(session.actualStartAt),
    contextConflict:activeStageConflict?'active_stage_mismatch':null,
    activeStage:stage?Object.freeze({
      id:String(stage.id),
      typeKey:text(stage.typeKey||stage.type_key,60),
      label:text(stage.nameOverride||stage.name||stage.typeKey||stage.type_key,120),
      startedAt:stageStartedAt,
      elapsedMinutes,
      targetChamberCelsius:number(target?.targetChamberCelsius??target?.target_chamber_c),
      minChamberCelsius:number(target?.minChamberCelsius??target?.min_chamber_c),
      maxChamberCelsius:number(target?.maxChamberCelsius??target?.max_chamber_c),
      plannedDurationMinutes:number(target?.plannedDurationMinutes??target?.planned_duration_minutes)
    }):null,
    latestTemperatures:Object.freeze(latestTemperatures),
    recentEvents:Object.freeze(recentEvents)
  });
}

export function answerActiveSessionQuestion(bundle,prompt,options={}){
  const facts=buildActiveSessionFacts(bundle,options);
  const q=String(prompt??'').trim().toLocaleLowerCase('pl-PL');
  const claims=[];
  const links=[];
  const sessionEvidence=sessionRef(facts.sessionId,facts.actualStartAt);

  if(facts.contextConflict==='active_stage_mismatch'){
    claims.push(observed(
      'Dane bieżącego etapu są sprzeczne: zapis sesji i ostatnia zmiana etapu wskazują różne etapy. Nie wybieram jednego z nich jako faktu.',
      [sessionEvidence]
    ));
  }else if(facts.activeStage){
    const stage=facts.activeStage;
    const time=stage.elapsedMinutes==null
      ?'Czas etapu nie jest wiarygodnie zapisany.'
      :'Bieżący etap trwa '+stage.elapsedMinutes+' min.';
    claims.push(observed(
      'Aktualny etap: '+(stage.label||stage.typeKey||'nieznany')+'. '+time,
      [sessionEvidence,stageRef(facts.sessionId,stage.id,stage.startedAt)]
    ));
    links.push(Object.freeze({kind:'stage',ref:facts.sessionId+'/'+stage.id,label:'Otwórz bieżący etap'}));
  }else{
    claims.push(observed(
      'W tej sesji nie ma jednoznacznie zapisanego aktywnego etapu.',
      [sessionEvidence]
    ));
  }

  if(/temperatur|temp|ciep/i.test(q)){
    const readings=Object.entries(facts.latestTemperatures);
    if(readings.length===0){
      claims.push(observed(
        'Brak zapisanej aktualnej temperatury w tej sesji.',
        [sessionEvidence]
      ));
    }else{
      for(const [kind,row] of readings.slice(0,3)){
        claims.push(observed(
          'Ostatnia temperatura '+kind+': '+row.valueCelsius+'°C, zapis '+row.recordedAt
            +(row.stale?' — odczyt jest stary, więc nie traktuję go jako bieżącego.':'.'),
          [sessionEvidence]
        ));
      }
    }
  }

  if(/co.*(zrobi|robi)|what.*(did|done)|histori|zdarzen/i.test(q)){
    if(facts.recentEvents.length===0){
      claims.push(observed('Brak ostatnich zapisanych zdarzeń w tej sesji.',[sessionEvidence]));
    }else{
      const summary=facts.recentEvents.slice(0,5)
        .map(row=>row.eventType+' @ '+row.occurredAt).join('; ');
      claims.push(observed('Ostatnio zapisane zdarzenia: '+summary+'.',[sessionEvidence]));
    }
  }

  if(claims.length===0){
    claims.push(observed(
      'Sesja ma status '+(facts.status||'nieznany')+'.',
      [sessionEvidence]
    ));
  }

  links.push(Object.freeze({kind:'session',ref:facts.sessionId,label:'Otwórz to wędzenie'}));
  return Object.freeze({claims:Object.freeze(claims),links:Object.freeze(links),facts});
}
